import { alpha } from '@mui/material/styles';

import type { SpacingTokens } from '../../../types/SpacingTokens';
import { colors } from './variables';

export const spacing = 4;

export const breakpoints = {
	mobile: 743,
	tablet: 1023,
	desktop: 1920,
	sidebarBreakpoint: 1216,
};

const getSpace = (multiplier: number) => multiplier * spacing + 'px';

export const getFontProps = (fontFamily: string, fontWeight: number, fontSize: string) => ({
	fontFamily,
	fontWeight,
	fontSize,
});

export const spaces: SpacingTokens = {
	xxxs: getSpace(0.5), // 0.5 * 4 = 2px
	xxs: getSpace(1), // 1 * 4 = 4px
	xs: getSpace(2), // 2 * 4 = 8px
	sm: getSpace(3), // 3 * 4 = 12px
	md: getSpace(4), // 4 * 4 = 16px
	lg: getSpace(6), // 6 * 4 = 24px
	xl: getSpace(8), // 8 * 4 = 32px
	xxl: getSpace(10), // 10 * 4 = 40px
	xxxl: getSpace(12), // 12 * 4 = 48px
	xxxxl: getSpace(16), // 16 * 4 = 64px
	xxxxxl: getSpace(20), // 20 * 4 = 80px
};

export const borderRadius = {
	xxs: getSpace(1), // 1 * 4 = 4px
	xs: getSpace(2), // 2 * 4 = 8px
	sm: getSpace(3), // 3 * 4 = 12px
	md: getSpace(4), // 4 * 4 = 16px
	lg: getSpace(6), // 6 * 4 = 24px
	xl: getSpace(8), // 6 * 8 = 32px
	full: getSpace(90), // 90 * 4 = 360px
};

export const backgroundGradients = {
	aiAssistant: `linear-gradient(187deg, ${colors.violet50} -6.07%, ${colors.blue100} 138.33%);`,
};

export const mobileViewportFullHeight = 'calc(var(--vh, 1vh) * 100)';

export const palette = {
	common: {
		black: colors.neutral900,
		white: colors.neutral50,
	},
	primary: {
		main: colors.blue500,
		dark: colors.blue600,
		light: colors.blue400,
		contrastText: colors.blue50,
		contrast: colors.blue50,
		hover: alpha(colors.blue500, 0.04),
		outlinedBorder: alpha(colors.blue500, 0.48),
		selected: alpha(colors.blue500, 0.08),
		focusVisible: alpha(colors.blue500, 0.32),
	},
	secondary: {
		main: colors.violet800,
		dark: colors.violet900,
		light: colors.violet700,
		hover: alpha(colors.violet800, 0.04),
		contrastText: colors.violet50,
	},
	error: {
		main: colors.red600,
		dark: colors.red700,
		light: colors.red500,
		contrastText: colors.red50,
	},
	warning: {
		main: colors.orange600,
		dark: colors.orange700,
		light: colors.orange500,
		contrastText: colors.orange50,
	},
	info: {
		main: colors.lightblue500,
		dark: colors.lightblue600,
		light: colors.lightblue400,
		contrastText: colors.lightblue50,
	},
	success: {
		main: colors.green600,
		dark: colors.green700,
		light: colors.green500,
		contrastText: colors.green50,
	},
	accent: {
		pink: colors.pink500,
	},
	text: {
		primary: colors.violet800,
		secondary: colors.violet700,
		disabled: alpha(colors.violet800, 0.48),
		focusVisible: alpha(colors.violet800, 0.32),
		selected: alpha(colors.violet800, 0.08),
		visited: colors.violet500,
		outlineBorder: colors.blue550,
	},
	background: {
		default: colors.neutral100,
		paperElevation0: colors.neutral100,
		paperElevation1: colors.neutral50,
		paperElevation2: colors.neutral0,
	},
	action: {
		active: alpha(colors.neutral900, 0.56),
		hover: alpha(colors.neutral800, 0.04),
		selected: alpha(colors.neutral900, 0.08),
		disabled: alpha(colors.neutral900, 0.4),
		disabledBackground: alpha(colors.neutral900, 0.12),
		focus: alpha(colors.neutral900, 0.12),
	},
	components: {
		tooltip: {
			fill: colors.neutral500,
		},
		chip: {
			defaultEnabled: colors.neutral200,
			defaultCloseFill: alpha(colors.neutral900, 0.26),
		},
		alert: {
			info: {
				color: colors.lightblue600,
			},
			warning: {
				background: colors.orange50,
				border: alpha(colors.orange600, 0.48),
			},
		},
		input: {
			outlined: {
				enabledBorder: alpha(colors.violet800, 0.16),
			},
		},
		backdrop: {
			fill: alpha(colors.neutral800, 0.48),
		},
	},
	divider: alpha(colors.neutral800, 0.12),
	elevation: {
		outlined: colors.neutral200,
		enabled: alpha(colors.violet800, 0.16),
	},
	accordion: {
		expandIcon: {
			color: alpha(colors.neutral800, 0.56),
		},
	},
};

// Todo: use this token as a typography when designers implement the alert/description typography
export const alertDescriptionTypography = {
	...getFontProps('Poppins, sans-serif', 500, '0.875rem'),
	lineHeight: '1.25rem',
	letterSpacing: '0.25px',
	fontStyle: 'normal',
};

export const typography = {
	fontFamily: 'Poppins, sans-serif', // Default font for all text
	h1: getFontProps('Borna, sans-serif', 500, '4.5rem'),
	h2: getFontProps('Borna, sans-serif', 400, '2.5rem'),
	h3: getFontProps('Borna, sans-serif', 400, '2rem'),
	h4: getFontProps('Borna, sans-serif', 700, '1.75rem'),
	h5: {
		...getFontProps('Borna, sans-serif', 600, '1.5rem'),
		lineHeight: '2rem',
	},
	h6: {
		...getFontProps('Borna, sans-serif', 600, '1.125rem'),
		lineHeight: '1.5rem',
		letterSpacing: '0.15px',
	},
	subtitle1: getFontProps('Borna, sans-serif', 400, '1rem'),
	subtitle2: {
		...getFontProps('Borna, sans-serif', 400, '0.875rem'),
		lineHeight: '1.5rem',
		letterSpacing: '0.1px',
	},
	subtitle3: {
		...getFontProps('Borna, sans-serif', 600, '0.75rem'),
		lineHeight: '1rem',
		letterSpacing: '0.1px',
	},
	formLabel: {
		...getFontProps('Borna, sans-serif', 500, '1.125rem'),
		lineHeight: '1.75rem',
		letterSpacing: '0.00938rem',
	},
	body1: {
		...getFontProps('Poppins, sans-serif', 300, '1rem'),
		color: palette?.text?.secondary,
		lineHeight: '1.5rem',
		letterSpacing: '0.5px',
	},
	body2: {
		...getFontProps('Poppins, sans-serif', 300, '0.875rem'),
		color: palette?.text?.secondary,
		lineHeight: '1.25rem',
		letterSpacing: '0.25px',
	},
	overline: {
		...getFontProps('Poppins, sans-serif', 400, '0.75rem'),
		letterSpacing: '1.5px',
		lineHeight: '1rem',
	},
	caption: {
		...getFontProps('Poppins, sans-serif', 400, '0.75rem'),
		lineHeight: '1rem',
		letterSpacing: '0.4px',
	},
	caption2: {
		...getFontProps('Poppins, sans-serif', 400, '0.625rem'),
		lineHeight: '1rem',
		letterSpacing: '0.004px',
	},
	alert: {
		title: {
			...getFontProps('Borna, sans-serif', 500, '1rem'),
			lineHeight: '1.5rem',
			letterSpacing: '0.15px',
		},
	},
	chip: {
		label: {
			...getFontProps('Poppins, sans-serif', 500, '0.75rem'),
			lineHeight: '1.125rem',
			letterSpacing: '0.16px',
		},
	},
	button: {
		small: {
			...getFontProps('Poppins, sans-serif', 500, '0.75rem'),
			lineHeight: '1.375rem',
			letterSpacing: '0.46px',
		},
		alert: {
			...getFontProps('Poppins, sans-serif', 500, '1rem'),
			lineHeight: '1.625rem',
			letterSpacing: '0.46px',
		},
	},
	input: {
		value: {
			...getFontProps('Poppins, sans-serif', 400, '1rem'),
			lineHeight: '1.5rem',
			letterSpacing: '0.15px',
		},
	},
	menu: {
		itemDense: {
			...getFontProps('Poppins, sans-serif', 300, '0.75rem'),
			lineHeight: '1.25rem',
			letterSpacing: '0.17px',
		},
		sectionHeader: {
			...getFontProps('Poppins, sans-serif', 400, '0.625rem'),
			lineHeight: '1rem',
			letterSpacing: '0.4px',
		},
	},
};

export const topBarHeight = 16 * spacing + 'px';
export const sideBarWidth = 16 * spacing + 'px';

export const cardDimensionsMedia = {
	desktop: {
		minWidth: 736,
		maxWidth: 928,
	},
	tablet: {
		minWidth: 712,
		maxWidth: 928,
	},
	mobile: {
		minWidth: 288,
		maxWidth: 711,
	},
};

export const cardDimensions = {
	short: {
		height: 132,
	},
	long: {
		height: 280,
	},
};

export const dashboardCardDimensions = {
	width: 218,
	height: 390,
	coverImageHeight: 274,
	coverImageWidth: 194,
	video: {
		width: 276,
		height: 249,
		coverImageHeight: 157,
		coverImageWidth: 252,
	},
};

export const tooltipArrow = {
	width: 15,
	height: 11,
};

export const customSizes = {
	assistantNewChatDialog: {
		desktopWidth: 472,
	},
	assistantDeleteChatDialog: {
		desktopWidth: 424,
	},
	assistantLoomVideoDialog: {
		desktopWidth: '75%',
	},
	aiAssistantForm: {
		containerMaxWidth: '1440px',
		innerMaxWidth: '1080px',
	},
	coursesLimitDialog: {
		desktopWidth: 444,
	},
	exportDialog: {
		pagesSelectionStageWidth: '960px',
		lernsetSelectionStageWidth: '700px',
		lernsetsListHeight: '274px',
	},
	filtersDialog: {
		desktopWidth: '900px',
		desktopMargin: spaces.xxl,
		tabletMargin: spaces.lg,
		mobileTopMargin: '44px',
	},
	filtersFeedbackDialog: {
		desktopWidth: 458,
	},
	createCollectionDialog: {
		width: 900,
	},
	readerPage: {
		paddingX: spaces.md,
		paddingY: spaces.md,
		video: {
			maxWidth: '1052px',
			maxHeight: '592px',
		},
	},
	readerDetails: {
		width: '340px',
		headerHeight: '48px',
		publisherLogoHeight: '96px',
		publisherLogoWidth: '128px',
	},
	pagePreviewList: {
		width: '102px',
	},
	pdfArea: {
		paddingX: spaces.md,
	},
	floatingToolbar: {
		height: '48px',
		padding: spaces.xs,
	},
	searchInput: {
		width: {
			desktop: '288px',
			tablet: '288px',
			mobile: '259px',
		},
		height: '32px',
	},
	readerToolbarHeight: '65px',
	intercomOffset: {
		x: '106px',
	},
	sideNavbar: {
		expandedWidth: '256px',
		collapsedWidth: '48px',
		headerHeight: {
			mobile: '56px',
			desktop: '64px',
		},
		itemHeight: '32px',
	},
	swipeableDrawer: {
		dragIndicatorWidth: '40px',
		dragIndicatorHeight: '6px',
		height: '588px',
	},
	pricingPackage: {
		width: '368px',
	},
	gridMargins: spaces.md,
	pricing: {
		faq: {
			paddingTop: '60px',
			width: {
				desktop: '752px',
				mobile: '100%',
			},
		},
	},
	paymentMethods: {
		sepaForm: {
			height: '160px',
		},
		creditCardForm: {
			height: {
				desktop: '425px',
				mobile: '465px',
			},
		},
	},
	paymentInformationContainer: {
		paddingY: '120px',
		width: {
			desktop: '454px',
			mobile: '343px',
		},
	},
	paymentRhythmSelect: {
		width: {
			desktop: '227px',
			mobile: '171.5px',
		},
	},
	subjectsDialog: {
		width: {
			desktop: '472px',
		},
	},
	ownCollectionOverview: {
		watchlistWidth: '268px',
		bodyHeight: 'calc(100vh - 176px)', // 100vh - TopBarContent(64px) - PageTitle(48px) - CollectionsTabs(64px)
	},
	emptyStateSubtitleMaxWidth: '448px',
	attachmentsRowHeight: 82,
	paddingBottomLayoutWithIntercomeButton: '80px',
	dialog: {
		maxWidth: '600px',
		maxVideoWidth: '900px',
		maxWidthXs: '444px',
		maxImageHeight: '318px',
		maxImageHeightXs: '235px',
	},
	artifactCard: {
		cardHeight: '200px',
		avatarSize: 40,
		iconSize: 24,
	},
	imagePreviewOverlay: {
		buttonSize: 48,
		imageMaxWidth: '95vw',
		imageMaxHeight: '95vh',
	},
};

interface BoxShadowsType {
	elevation1: string;
	elevation2: string;
	elevation3: string;
	elevation4: string;
	elevation6: string;
	elevation8: string;
	elevation10: string;
	elevation12: string;
	elevation16: string;
	elevation24: string;
	elevation26: string;
}

export const boxShadows: BoxShadowsType = {
	elevation1: '0px 1px 2px 0px rgba(17, 24, 39, 0.08)',
	elevation2: '0px 3px 2px 0px rgba(17, 24, 39, 0.08)',
	elevation3: '0px 2px 4px -2px rgba(17, 24, 39, 0.05), 0px 4px 6px -1px rgba(17, 24, 39, 0.10)',
	elevation4: '0px 7px 10px -2px rgba(17, 24, 39, 0.10), 0px 3px 5px -1px rgba(17, 24, 39, 0.05)',
	elevation6: '0px 4px 6px 0px rgba(17, 24, 39, 0.05), 0px 10px 15px -3px rgba(17, 24, 39, 0.10)',
	elevation8: '0px 7px 8px 0px rgba(17, 24, 39, 0.05), 0px 15px 20px -4px rgba(17, 24, 39, 0.10)',
	elevation10:
		'0px 6px 6px -3px rgba(0, 0, 0, 0.20), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)',
	elevation12: '0px 10px 10px 0px rgba(17, 24, 39, 0.04), 0px 20px 25px -5px rgba(17, 24, 39, 0.10)',
	elevation16: '0px 13px 18px 0px rgba(17, 24, 39, 0.08), 0px 23px 36px -8px rgba(17, 24, 39, 0.10)',
	elevation24:
		'0px 16px 25px 0px rgba(17, 24, 39, 0.10), 0px 25px 50px -12px rgba(17, 24, 39, 0.25)',
	elevation26: '0px 10px 15px -3px rgba(17, 24, 39, 0.10), 0px 4px 6px 0px rgba(17, 24, 39, 0.05)',
};

export const customColors = {
	lernsetListItem: {
		border: alpha(colors.violet800, 0.48),
		selectedBorder: alpha(colors.blue500, 0.48),
		selectedBackground: alpha(colors.blue500, 0.08),
	},
};

export const fontWeights = {
	light: 300,
	regular: 400,
	medium: 500,
	semibold: 600,
	bold: 700,
};

export const fontFeatureSettings = {
	disableLigatures: "'liga' off, 'clig' off",
};
